import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery } from "react-query"
import { useParams } from "react-router-dom"

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import CloseIcon from "@mui/icons-material/Close"
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import { Card, Grid, IconButton, Skeleton } from "@mui/material"
import { Button, type InputChangeEvent, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { SelectWithSearch } from "../../../components/form/SelectWithSearch/SelectWithSearch"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { PaginatedScoreCardSetListList } from "../../../types/generated/api/PaginatedScoreCardSetListList"
import type { ProgramItem } from "../../../types/generated/api/ProgramItem"
import type { ScoreCardSetList } from "../../../types/generated/api/ScoreCardSetList"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"
import { getNonSelectedItems } from "../helpers"

import styles from "../Programs.module.scss"

// type for URL params
type ParamsType = {
  id: string
}

interface ScoreCardSetNodeType {
  scorecardset?: { name: string; uuid: string | undefined }
  isEdit?: boolean
  menuView?: boolean
  isScorecardSetsLoading?: Boolean
  currentIndex?: number
  selectedItems?: Array<ProgramItem>
  handleRemove?: () => void
  onValueChange?: (scorecardset: { name: string; uuid: string }) => void
}

/**
 * ScoreCardSetCard component
 * @param {string} uuid
 * @param {boolean} isEdit
 * @param {boolean} menuView
 * @param {boolean} isScorecardSetsLoading
 * @param {Array<ScoreCardItem>} ScorecardSets
 * @param {function} handleRemove
 * @param {function} onValueChange select value change
 * @returns {React.ReactElement}
 */
export function ScoreCardSetBlock(props: Readonly<ScoreCardSetNodeType>): React.ReactElement {
  const {
    isEdit,
    scorecardset,
    menuView,
    currentIndex,
    selectedItems,
    isScorecardSetsLoading,
    handleRemove,
    onValueChange,
  } = props
  const { id: projectId } = useParams<ParamsType>()

  const readMode = isEdit ?? null

  const theme = getTheme()

  const { t } = useTranslation("common")

  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)

  const {
    data: scorecardsets,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScoreCardSetListList>, AxiosError, ScoreCardSetList>(
    ["scorecardsets", projectId, searchValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchScorecardsets({ projectId: projectId as string, page: pageParam, search: searchValue }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ScoreCardSetList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ScoreCardSetList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
    },
  )

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastMenuItemRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  return (
    <Card className={menuView ? styles.ScoreCardSetCard_menuView : styles.ScoreCardSetCard}>
      <Grid container wrap="nowrap">
        <Grid item>
          <Grid item className={styles.ScoreCardSetCardType}>
            <AssignmentOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />
          </Grid>
        </Grid>
        <Grid
          item
          display={"flex"}
          flexGrow={1}
          flexDirection={"column"}
          paddingLeft={1}
          justifyContent="space-between"
          alignItems="flex-start"
          overflow={"hidden"}
        >
          <Grid item container justifyContent="space-between">
            <Grid item>
              <Typography variant="label-bold" variantColor={2}>
                {t("scoreCardSetBlock.scoreCardsets")}
              </Typography>
            </Grid>
            {readMode && (
              <Grid item justifySelf={"end"}>
                <IconButton style={{ marginTop: "-15px" }} size="small" onClick={() => handleRemove?.()}>
                  <CloseIcon fontSize="small" className={styles.remove} />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            container
            alignItems="flex-start"
            justifyContent={"space-between"}
            marginTop={0.5}
            wrap="nowrap"
          >
            <Grid item xs={9}>
              {isScorecardSetsLoading ? (
                <Skeleton height={"25px"} width="100px" />
              ) : isEdit || menuView ? (
                <SelectWithSearch
                  menuTriggerProps={{
                    fullWidth: true,
                    hideDescription: true,
                    placeholder: t("scoreCardSetBlock.searchPlaceHolder"),
                    disabled: menuView || scorecardsets?.pages.length === 0,
                  }}
                  isOptionsLoading={isFetching || isFetchingNextPage}
                  searchInputPlaceholder={t("searchSortFilter.search", { ns: "form" })}
                  lastMenuItemRef={lastMenuItemRef}
                  onSelectMenuItem={({ text, value }) => onValueChange?.({ uuid: value, name: text })}
                  optionsList={
                    typeof currentIndex === "number"
                      ? getNonSelectedItems(currentIndex, scorecardsets?.pages ?? [], selectedItems ?? []).map(
                          (project) => {
                            return {
                              value: project.uuid,
                              text: project.name,
                              disabled: project.hidden,
                              item: (
                                <Typography variant="a" variantColor={1}>
                                  {project.name}
                                </Typography>
                              ),
                            }
                          },
                        )
                      : []
                  }
                  inputValue={
                    scorecardset?.uuid?.startsWith("default-")
                      ? t("scoreCardSetBlock.searchPlaceHolder")
                      : (scorecardset?.name ?? "")
                  }
                  searchInputChangeHandler={(e: InputChangeEvent) => {
                    setSearchValue(e.target.value.toString())
                  }}
                  onMenuClose={() => setSearchValue(undefined)}
                />
              ) : (
                <Typography variant="h3-bold" style={{ width: "100%", textOverflow: "ellipsis" }} noWrap>
                  {scorecardset?.name}
                </Typography>
              )}
            </Grid>

            <Grid item display={"flex"} alignSelf={"center"} justifySelf={"end"} ml={1} pb={"1px"}>
              <Button
                variant="secondary"
                /*Workaround to use window.open in safari */
                onClick={() =>
                  setTimeout(() => {
                    window.open(`/projects/${projectId}/Scorecards`, "_blank")
                  })
                }
                size="small"
                disabled={menuView}
              >
                <LaunchOutlinedIcon
                  fontSize="medium"
                  sx={{ color: menuView ? theme.palette.gray.background[1] : theme.palette.base.text[1] }}
                />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}
